import {
  BLISS_BASIC_ACCESS_OPTIONS,
  BLISS_BASIC_FINISH_OPTIONS,
  BLISS_BASIC_STORAGE_OPTIONS,
  type BlissBasicConfiguratorStepId,
} from '../config/blissBasicConfigurator'
import { BLISS_BASIC_MODEL_OPTIONS, BLISS_BASIC_TIMELINE_OPTIONS } from '../config/blissBasic'
import type { BlissBasicConfiguratorChoice, BlissBasicConfiguratorStepDef } from '../config/blissBasicCatalog'

export const BLISS_BASIC_CONFIGURATOR_STEP_IDS = [
  'layout',
  'finish',
  'access',
  'storage',
  'sleepers',
  'timeline',
] as const

export type BlissBasicConfiguratorCatalogStepId = (typeof BLISS_BASIC_CONFIGURATOR_STEP_IDS)[number]

export function defaultConfiguratorStepDefs(): BlissBasicConfiguratorStepDef[] {
  return [
    {
      id: 'layout',
      title: 'Layout',
      subtitle: 'Pick a layout by comparing standard mattress footprints',
      multiSelect: false,
      kind: 'choices',
      items: BLISS_BASIC_MODEL_OPTIONS.map((m) => ({
        choiceKey: m.id,
        label: m.label,
        detail: m.detail,
        priceUsd: null,
        priceHighUsd: null,
        priceLabel: null,
        photoUrl: null,
      })),
    },
    {
      id: 'finish',
      title: 'Finish',
      subtitle: 'Gloss White or Matte White · other shades and stains with Bliss Custom',
      multiSelect: false,
      kind: 'choices',
      items: BLISS_BASIC_FINISH_OPTIONS.map((o) => ({
        choiceKey: o.id,
        label: o.label,
        detail: o.hint,
        priceUsd: null,
        priceHighUsd: null,
        priceLabel: null,
        photoUrl: null,
      })),
    },
    {
      id: 'access',
      title: 'Access',
      subtitle: 'Stairs or ladder',
      multiSelect: false,
      kind: 'choices',
      items: BLISS_BASIC_ACCESS_OPTIONS.map((o) => ({
        choiceKey: o.id,
        label: o.label,
        detail: o.hint,
        priceUsd: null,
        priceHighUsd: null,
        priceLabel: null,
        photoUrl: null,
      })),
    },
    {
      id: 'storage',
      title: 'Storage',
      subtitle: 'Optional — select any that fit',
      multiSelect: true,
      kind: 'choices',
      items: BLISS_BASIC_STORAGE_OPTIONS.map((o) => ({
        choiceKey: o.id,
        label: o.label,
        detail: o.hint,
        priceUsd: null,
        priceHighUsd: null,
        priceLabel: null,
        photoUrl: null,
      })),
    },
    {
      id: 'sleepers',
      title: 'Occupancy',
      subtitle: 'How many people need to sleep here?',
      multiSelect: false,
      kind: 'sleepers',
      items: [2, 3, 4, 5, 6, 7, 8].map((n) => ({
        choiceKey: String(n),
        label: String(n),
        detail: `${n} beds`,
        priceUsd: null,
        priceHighUsd: null,
        priceLabel: null,
        photoUrl: null,
      })),
    },
    {
      id: 'timeline',
      title: 'Timeline',
      subtitle: 'When are you hoping to install?',
      multiSelect: false,
      kind: 'timeline',
      items: BLISS_BASIC_TIMELINE_OPTIONS.map((o) => ({
        choiceKey: o.value,
        label: o.label,
        detail: null,
        priceUsd: null,
        priceHighUsd: null,
        priceLabel: null,
        photoUrl: null,
      })),
    },
  ]
}

export function mergeConfiguratorSteps(
  fromApi: BlissBasicConfiguratorStepDef[] | undefined,
  stepCopy: Partial<Record<BlissBasicConfiguratorCatalogStepId, { title?: string; subtitle?: string }>>,
): BlissBasicConfiguratorStepDef[] {
  const defaults = defaultConfiguratorStepDefs()
  if (!fromApi?.length) {
    return defaults.map((d) => ({
      ...d,
      title: stepCopy[d.id]?.title ?? d.title,
      subtitle: d.id === 'finish' ? d.subtitle : stepCopy[d.id]?.subtitle ?? d.subtitle,
    }))
  }

  return defaults.map((def) => {
    const remote = fromApi.find((s) => s.id === def.id)
    let items =
      remote && remote.items.length > 0
        ? remote.items.filter((i) => i.active !== false && i.inStock !== false)
        : def.items
    if (def.id === 'finish') {
      // Older catalogs may still advertise stains. Only the three Basic paints
      // are selectable; retain published pricing/availability for matching IDs.
      items = def.items.map(item => ({
        ...item,
        ...remote?.items.find(remoteItem => remoteItem.choiceKey === item.choiceKey),
        label: item.label,
        detail: item.detail,
      })).filter(item => item.active !== false && item.inStock !== false)
    }
    return {
      ...def,
      title: stepCopy[def.id]?.title ?? remote?.title ?? def.title,
      subtitle: def.id === 'finish' ? def.subtitle : stepCopy[def.id]?.subtitle ?? remote?.subtitle ?? def.subtitle,
      multiSelect: remote?.multiSelect ?? def.multiSelect,
      kind: remote?.kind ?? def.kind,
      items,
    }
  })
}

export function formatChoicePrice(choice: BlissBasicConfiguratorChoice): string | null {
  if (choice.priceUsd == null && choice.priceHighUsd == null) return null
  const fmt = (n: number) => `$${n.toLocaleString()}`
  const prefix = choice.priceLabel?.trim() || 'Starting at'
  if (choice.priceUsd != null && choice.priceHighUsd != null && choice.priceHighUsd > choice.priceUsd) {
    return `${prefix} ${fmt(choice.priceUsd)} – ${fmt(choice.priceHighUsd)}`
  }
  if (choice.priceUsd != null) return `${prefix} ${fmt(choice.priceUsd)}`
  if (choice.priceHighUsd != null) return `${prefix} ${fmt(choice.priceHighUsd)}`
  return null
}

export function isConfiguratorSelectionStep(
  id: BlissBasicConfiguratorStepId,
): id is BlissBasicConfiguratorCatalogStepId {
  return (BLISS_BASIC_CONFIGURATOR_STEP_IDS as readonly string[]).includes(id)
}
